<script setup>
import { computed, reactive, ref } from "vue";
import NewInput from "../forms/NewInput.vue";
import NewAutoselect from "../forms/NewAutoselect.vue";
import Tiptap from "../forms/Tiptap.vue";
import emitter from "tiny-emitter/instance";

const modal = reactive({
    isOpen: false,
});
const tiptapRef = ref(null);
const formRef = ref(null);

const category = ref(null);

const props = defineProps({
    categories: Array,
    whiteboardUrl: String,
    context: String,
    action: String,
    csrf: String,
    playApi: String,
    playbookApi: String,
    previewUrl: String,
    whiteboardApi: String,
    sport: String,
});

emitter.on("toggle-thread-modal", (data) => {
    modal.isOpen = data;
});

const errors = reactive({
    title: null,
    content: null,
    category: null,
});

const categoryMap = computed(() => {
    const map = new Map();
    props.categories?.forEach((category) => {
        map.set(category.id, category);
    });
    return map;
});
const mappedCategories = computed(() => {
    const filtered = props.categories?.filter(
        (category) =>
            !category.parent_category_id ||
            categoryMap.value.get(category.parent_category_id != null)
    );
    return (
        filtered
            ?.map((category) => {
                const label = category.parent_category_id
                    ? `${
                          categoryMap.value.get(category.parent_category_id)
                              .title
                      } • ${category.title}`
                    : category.title;
                return {
                    label: label,
                    value: category.id,
                };
            })
            .sort((a, b) => {
                return a.label.localeCompare(b.label);
            }) ?? []
    );
});

function submit(e) {
    e.preventDefault();

    errors.title = null;
    errors.content = null;
    errors.category = null;

    const data = new FormData(formRef.value);
    if (tiptapRef.value.getText().length < 10) {
        errors.content = "Your content is too short (min 10 characters)";
    }
    if (data.get("title").length < 5) {
        errors.title = "Your title is too short (min 5 characters)";
    }
    if (data.get("category") === null || data.get("category") === "") {
        errors.category = "You must select a category";
    }
    if (errors.title || errors.content || errors.category) {
        return;
    }
    tiptapRef.value.submit({
        target: {
            form: formRef.value,
        },
    });
}

const teamId = computed(() => {
    return new URLSearchParams(window.location.search).get("teamId");
});
</script>

<template>
    <button
        type="button"
        @click="modal.isOpen = true"
        class="
            md:ml-auto
            flex
            py-2
            px-4
            border
            font-bold
            border-accent
            rounded-full
            text-accent
            items-center
        "
    >
        <ion-icon name="add-outline" class="text-xl mr-2"></ion-icon>
        Create New Thread
    </button>
    <teleport to="body">
        <div
            v-show="modal.isOpen"
            class="
                bg-black bg-opacity-25
                fixed
                top-0
                left-0
                w-full
                h-full
                z-[1000]
                flex
                items-center
                justify-center
            "
        >
            <div
                class="
                    bg-white
                    rounded-lg
                    shadow-lg
                    p-4
                    relative
                    font-sans
                    min-w-[70vw]
                    max-w-[70vw]
                "
            >
                <header class="flex items-center mb-2">
                    <h3 class="mr-12 text-2xl font-semibold">
                        Create a New Thread
                    </h3>
                    <button
                        type="button"
                        class="
                            rounded-md
                            ml-auto
                            leading-[0]
                            p-1
                            bg-gray-100
                            hover:bg-gray-200
                            text-xl
                        "
                        @click="modal.isOpen = false"
                    >
                        <ion-icon name="close"></ion-icon>
                    </button>
                </header>
                <div class="text-gray-500">
                    Enter the information to create your thread
                </div>

                <hr class="my-6" />
                <form
                    ref="formRef"
                    @submit="submit"
                    :action="action"
                    method="post"
                    enctype="multipart/form-data"
                >
                    <div class="max-h-[65vh] overflow-scroll">
                        <input type="hidden" name="team_id" :value="teamId" />
                        <input type="hidden" name="_token" :value="csrf" />
                        <div class="my-4">
                            <div
                                class="text-xs my-2 p-2 bg-red-100 text-red-800"
                                v-if="errors.title"
                            >
                                {{ errors.title }}
                            </div>
                            <label
                                class="font-semibold block mb-2 text-sm"
                                for="title"
                                >Thread Name</label
                            >
                            <NewInput
                                type="text"
                                name="title"
                                placeholder="Enter thread name"
                                required
                                class="mb-4"
                            />
                        </div>
                        <div class="my-4">
                            <div
                                class="text-xs my-2 p-2 bg-red-100 text-red-800"
                                v-if="errors.category"
                            >
                                {{ errors.category }}
                            </div>
                            <label
                                class="font-semibold block mb-2 text-sm"
                                for="title"
                                >Topic</label
                            >
                            <NewAutoselect
                                name="category"
                                placeholder="Select a topic"
                                v-model="category"
                                :options="mappedCategories"
                            />
                        </div>
                        <div class="my-8 relative">
                            <div
                                class="text-xs my-2 p-2 bg-red-100 text-red-800"
                                v-if="errors.content"
                            >
                                {{ errors.content }}
                            </div>
                            <Tiptap
                                @submit="finalizeSubmit"
                                ref="tiptapRef"
                                :show-submit="false"
                                :context="null"
                                :whiteboard-url="whiteboardUrl"
                                :play-api="playApi"
                                :playbook-api="playbookApi"
                                :preview-url="previewUrl"
                                :whiteboard-api="whiteboardApi"
                                :is-modal="true"
                                :sport="sport"
                                name="content"
                                placeholder="Write your reply ..."
                            ></Tiptap>
                        </div>
                    </div>
                    <div class="my-8 flex items-center justify-end gap-4">
                        <button
                            type="button"
                            class="
                                flex
                                py-2
                                px-6
                                border
                                font-bold
                                border-accent
                                rounded-full
                                text-accent
                                items-center
                            "
                            @click="modal.isOpen = false"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            class="
                                flex
                                py-2
                                px-4
                                bg-primary
                                font-bold
                                rounded-full
                                text-white
                                items-center
                            "
                        >
                            Create a Thread
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </teleport>
</template>

<style scoped></style>
